import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Container,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Spinner,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  useColorModeValue,
  useToast,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useMemo, useState } from "react"

import { UsersService } from "../../client"

export const Route = createFileRoute("/_layout/settings")({
  component: SettingsPage,
})

function SettingsPage() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const panelBg = useColorModeValue("white", "gray.800")
  const panelBorder = useColorModeValue("gray.200", "whiteAlpha.200")
  const statBg = useColorModeValue("white", "gray.800")
  const statBorder = useColorModeValue("gray.200", "whiteAlpha.200")
  const statLabelColor = useColorModeValue("gray.600", "gray.300")
  const statValueColor = useColorModeValue("gray.900", "white")

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["users", "me"],
    queryFn: () => UsersService.readUserMe(),
  })

  const [email, setEmail] = useState("")
  const [currentPassword, setCurrentPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmNewPassword, setConfirmNewPassword] = useState("")

  const emailFromApi = useMemo(() => data?.email ?? "", [data])

  const updateEmailMutation = useMutation({
    mutationFn: (nextEmail: string) =>
      UsersService.updateUserMe({ requestBody: { email: nextEmail } }),
    onSuccess: async (user) => {
      await queryClient.invalidateQueries({ queryKey: ["users", "me"] })
      setEmail(user?.email ?? "")
      toast({
        title: "E-mail zaktualizowany",
        status: "success",
        duration: 2500,
        isClosable: true,
      })
    },
    onError: (e: any) => {
      toast({
        title: "Nie udało się zaktualizować e-maila",
        description: e?.body?.detail?.[0]?.msg ?? e?.body?.error ?? e?.message,
        status: "error",
        duration: 3500,
        isClosable: true,
      })
    },
  })

  const updatePasswordMutation = useMutation({
    mutationFn: () =>
      UsersService.updatePasswordMe({
        requestBody: {
          currentPassword,
          newPassword,
        },
      }),
    onSuccess: () => {
      setCurrentPassword("")
      setNewPassword("")
      setConfirmNewPassword("")
      toast({
        title: "Hasło zaktualizowane",
        status: "success",
        duration: 2500,
        isClosable: true,
      })
    },
    onError: (e: any) => {
      toast({
        title: "Nie udało się zaktualizować hasła",
        description: e?.body?.detail?.[0]?.msg ?? e?.body?.error ?? e?.message,
        status: "error",
        duration: 3500,
        isClosable: true,
      })
    },
  })

  if (isPending) {
    return (
      <Container maxW="4xl" py={8}>
        <Spinner />
      </Container>
    )
  }

  if (isError) {
    return (
      <Container maxW="4xl" py={8}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          Nie udało się pobrać profilu: {(error as any)?.message ?? "Błąd"}
        </Alert>
      </Container>
    )
  }

  const effectiveEmail = email || emailFromApi
  const hasPendingChanges = effectiveEmail.trim() !== emailFromApi
  const passwordReady =
    !!currentPassword &&
    !!newPassword &&
    !!confirmNewPassword &&
    newPassword === confirmNewPassword

  return (
    <Container maxW="4xl">
      <VStack spacing={4} align="stretch">
        <Box
          bgGradient="linear(to-r, cyan.600, blue.600)"
          color="white"
          borderRadius="2xl"
          px={{ base: 5, md: 8 }}
          py={{ base: 5, md: 7 }}
          boxShadow="lg"
        >
          <Flex
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
            gap={4}
          >
            <Box>
              <Heading size="lg">Mój profil</Heading>
              <Text mt={1} opacity={0.95}>
                Zarządzaj danymi konta i bezpieczeństwem dostępu.
              </Text>
            </Box>
            <Badge
              bg="whiteAlpha.250"
              color="white"
              borderRadius="full"
              px={3}
              py={1}
            >
              Settings
            </Badge>
          </Flex>
        </Box>

        <HStack spacing={3} align="stretch">
          <Stat
            bg={statBg}
            borderWidth="1px"
            borderColor={statBorder}
            borderRadius="xl"
            px={4}
            py={3}
          >
            <StatLabel color={statLabelColor}>Adres e-mail</StatLabel>
            <StatNumber color={statValueColor} fontSize="lg" noOfLines={1}>
              {emailFromApi || "—"}
            </StatNumber>
          </Stat>
          <Stat
            bg={statBg}
            borderWidth="1px"
            borderColor={statBorder}
            borderRadius="xl"
            px={4}
            py={3}
          >
            <StatLabel color={statLabelColor}>Zmiany e-mail</StatLabel>
            <StatNumber color={statValueColor}>
              {hasPendingChanges ? 1 : 0}
            </StatNumber>
          </Stat>
          <Stat
            bg={statBg}
            borderWidth="1px"
            borderColor={statBorder}
            borderRadius="xl"
            px={4}
            py={3}
          >
            <StatLabel color={statLabelColor}>Hasło gotowe</StatLabel>
            <StatNumber color={statValueColor}>
              {passwordReady ? 1 : 0}
            </StatNumber>
          </Stat>
        </HStack>

        <Box
          bg={panelBg}
          borderWidth="1px"
          borderColor={panelBorder}
          borderRadius="2xl"
          p={4}
        >
          <Text fontWeight="semibold" mb={3}>
            Zmiana e-maila
          </Text>
          <Stack spacing={3}>
            <FormControl>
              <FormLabel>Nowy e-mail</FormLabel>
              <Input
                type="email"
                value={effectiveEmail}
                onChange={(e) => setEmail(e.target.value)}
                borderRadius="lg"
              />
            </FormControl>
            <Button
              colorScheme="green"
              alignSelf="start"
              isLoading={updateEmailMutation.isPending}
              isDisabled={
                !effectiveEmail.trim() || effectiveEmail.trim() === emailFromApi
              }
              onClick={() => updateEmailMutation.mutate(effectiveEmail.trim())}
              borderRadius="xl"
            >
              Zapisz e-mail
            </Button>
          </Stack>
        </Box>

        <Box
          bg={panelBg}
          borderWidth="1px"
          borderColor={panelBorder}
          borderRadius="2xl"
          p={4}
        >
          <Text fontWeight="semibold" mb={3}>
            Zmiana hasła
          </Text>
          <Stack spacing={3}>
            <FormControl>
              <FormLabel>Aktualne hasło</FormLabel>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                borderRadius="lg"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Nowe hasło</FormLabel>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                borderRadius="lg"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Potwierdź nowe hasło</FormLabel>
              <Input
                type="password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                borderRadius="lg"
              />
            </FormControl>
            <Button
              colorScheme="green"
              alignSelf="start"
              isLoading={updatePasswordMutation.isPending}
              isDisabled={
                !currentPassword ||
                !newPassword ||
                !confirmNewPassword ||
                newPassword !== confirmNewPassword
              }
              onClick={() => {
                if (newPassword !== confirmNewPassword) {
                  toast({
                    title: "Hasła nie są takie same",
                    status: "warning",
                    duration: 2500,
                    isClosable: true,
                  })
                  return
                }
                updatePasswordMutation.mutate()
              }}
              borderRadius="xl"
            >
              Zapisz hasło
            </Button>
          </Stack>
        </Box>
      </VStack>
    </Container>
  )
}
